/**
 * Pure parser for workspace URLs under the static-export client SPA
 * (spec: workspace-static-spa-routing).
 *
 * The site ships as a Next.js static export; the tenant code is a DB-driven
 * *middle* URL segment that cannot be statically enumerated, so the whole
 * `/workspace` area is served by a single static shell and routed client-side
 * from `window.location`. This helper turns a pathname into the active tenant
 * code and the inner view key. It is trailing-slash tolerant (`trailingSlash:
 * true`) and never throws.
 */

export interface WorkspacePathInfo {
  /** True when the path is under the workspace base. */
  isWorkspace: boolean;
  /** True for the bare base (`/workspace`) with no tenant code. */
  isBare: boolean;
  /** The tenant code segment, URL-decoded, or null when bare/non-workspace. */
  tenantCode: string | null;
  /** Sub-path segments after the tenant code, e.g. `['settings','item-master']`. */
  segments: string[];
  /** Normalized registry key, e.g. `'settings/item-master'`; `''` for the home view. */
  subPathKey: string;
}

const EMPTY: WorkspacePathInfo = {
  isWorkspace: false,
  isBare: false,
  tenantCode: null,
  segments: [],
  subPathKey: '',
};

/**
 * Parse `pathname` against a workspace `base` (default `/workspace`).
 * `base` is given without a trailing slash, e.g. `/workspace` or `/welcome`.
 */
export function parseWorkspacePath(
  pathname: string,
  base = '/workspace',
): WorkspacePathInfo {
  const baseSeg = base.replace(/^\/+|\/+$/g, '');
  const parts = (pathname || '').replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);

  if (parts[0] !== baseSeg) return EMPTY;

  const tenantRaw = parts[1];
  if (!tenantRaw) {
    return { isWorkspace: true, isBare: true, tenantCode: null, segments: [], subPathKey: '' };
  }

  let tenantCode = tenantRaw;
  try {
    tenantCode = decodeURIComponent(tenantRaw);
  } catch {
    /* malformed escape — keep the raw value */
  }

  const segments = parts.slice(2);
  return {
    isWorkspace: true,
    isBare: false,
    tenantCode,
    segments,
    subPathKey: segments.join('/'),
  };
}
